// Contrato del blog. Lo cumple el JSON del backend (mantenido en admin.magrolabs)
// y lo consume el componente reutilizable para renderizar sin retrabajo.
//
// Convenciones:
// - URL pública del post: `/${section}/${slug}`.
// - `paragraphs` admite HTML inline. Enlaces internos a artículos inexistentes
//   llevan `data-exists="false"` y se renderizan como texto.
// - Imágenes: `url` es la final (bucket); `sourceUrl` es la del post original.

export type BlogSectionKey =
  | "guias"
  | "recetas"
  | "noticias"
  | "entrenamiento"
  | "mitos"
  | "sostenibilidad";

/** Etiquetas legibles por sección (breadcrumb, títulos de listado, "Ver todo"). */
export const SECTION_LABELS: Record<BlogSectionKey, string> = {
  guias: "Guías",
  recetas: "Recetas",
  noticias: "Noticias",
  entrenamiento: "Entrenamiento",
  mitos: "Mitos y verdades",
  sostenibilidad: "Sostenibilidad",
};

export interface BlogImage {
  url: string;
  sourceUrl?: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
}

export interface BlogSection {
  id: string;
  heading: string;
  paragraphs: string[];
  image?: BlogImage;
}

export interface BlogFaq {
  question: string;
  answer: string;
}

export interface BlogRelated {
  title: string;
  excerpt: string;
  minutes: number;
  url: string;
}

export interface BlogReference {
  title: string;
  url: string;
  source?: string;
}

export interface BlogAuthor {
  name: string;
  role: string;
  initials: string;
  avatarUrl?: string;
}

export interface BlogPost {
  slug: string;
  section: BlogSectionKey;
  lang: string;
  category: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  heroImage: BlogImage;
  author: BlogAuthor;
  /** fecha legible para mostrar (ej. "14 de junio de 2026") */
  publishedAt: string;
  /** fecha de publicación en ISO 8601 (para el schema BlogPosting) */
  datePublished: string;
  /** última modificación en ISO 8601 (opcional) */
  dateModified?: string;
  readingMinutes: number;
  sourceUrl?: string;
  sections: BlogSection[];
  faqs: BlogFaq[];
  related: BlogRelated[];
  references: BlogReference[];
}

// --- Vistas de listado (home / secciones) ---

/** Versión ligera para tarjetas de listado (sin el cuerpo del artículo). */
export interface BlogPostSummary {
  slug: string;
  section: BlogSectionKey;
  category: string;
  title: string;
  excerpt: string;
  heroImage: BlogImage;
  author: BlogAuthor;
  publishedAt: string;
  readingMinutes: number;
}

export interface BlogFeaturedSection {
  section: BlogSectionKey;
  label: string;
  posts: BlogPostSummary[];
}

/** Respuesta de la home del blog: último + destacados por sección + recientes. */
export interface BlogHome {
  latest: BlogPostSummary;
  featured: BlogFeaturedSection[];
  recent: BlogPostSummary[];
}

export interface BlogListResponse {
  posts: BlogPostSummary[];
  page: number;
  totalPages: number;
  total: number;
}
